/**
 * Pure scoring for assessments, driven by `assessment.metadata.scoring`.
 *
 * The reusable-assessment model stores answers as generic JSON; how a filled-in set turns into a
 * summary is per-assessment and lives in the assessment's `metadata`, not in code welded to one
 * questionnaire. This module implements the `maturity_stage` engine — a weighted-pillar maturity
 * model with a governance-floor cap — ported from the standalone `Workshop-assessment-temp` app so
 * the seeded "AI-Era Cyber Defense Assessment" demo produces the same shape of result inside the
 * toolbox. Other engines can be added to `scoreAssessment` behind the same `ScoreResult` contract.
 *
 * Everything here is pure (no fetching, no React) so it runs identically for the respondent's
 * post-submit summary and the admin's review of a stored response set, and so it is unit-testable.
 */

import { AssessmentQuestion } from './assessments';

// ---------------------------------------------------------------------------
// Config, mirrored from `assessment.metadata.scoring` (see the seed command)
// ---------------------------------------------------------------------------

export interface StageBand {
  stage: number;
  name: string;
  /** Inclusive upper bound of the mean maturity that still maps to this stage. */
  max: number;
}

export interface MaturityScoringConfig {
  engine: 'maturity_stage';
  /** The section whose questions are the scored maturity controls. */
  maturity_section_key: string;
  level_min: number;
  level_max: number;
  /** Relative weight per domain (parsed from the control key prefix, e.g. `D1`). */
  pillar_weights: Record<string, number>;
  domain_labels: Record<string, string>;
  /** Apply the "final stage cannot exceed the weakest pillar (or governance domain) + 1" cap. */
  governance_floor?: boolean;
  /** The domain that acts as the governance anchor for the floor (e.g. `D3`). */
  governance_domain?: string;
  stage_bands: StageBand[];
}

/** Any assessment's scoring config; only `maturity_stage` is implemented today. */
export type ScoringConfig = MaturityScoringConfig;

export const getScoringConfig = (assessment: { metadata?: any } | null | undefined): ScoringConfig | null => {
  const cfg = assessment?.metadata?.scoring;
  if (cfg && cfg.engine === 'maturity_stage' && Array.isArray(cfg.stage_bands)) return cfg as MaturityScoringConfig;
  return null;
};

// ---------------------------------------------------------------------------
// Result shape
// ---------------------------------------------------------------------------

export interface DomainScore {
  domain: string;
  label: string;
  /** Mean answered maturity level for the domain, in [level_min, level_max]. */
  mean: number;
  stage: number;
  stageName: string;
  answered: number;
  total: number;
}

export interface ControlScore {
  key: string;
  text: string;
  domain: string;
  /** The answered level, or null when the control was left unanswered. */
  level: number | null;
}

export interface ScoreResult {
  engine: 'maturity_stage';
  /** Weighted overall mean across domains, in [level_min, level_max]. */
  overallMean: number;
  /** Stage before the governance-floor cap is applied. */
  rawStage: number;
  /** Final stage after the cap. Equal to rawStage when the floor does not bind. */
  stage: number;
  stageName: string;
  /** What forced the cap, when it bound: 'governance' | 'weakest_pillar' | 'both' | null. */
  cappedBy: 'governance' | 'weakest_pillar' | 'both' | null;
  domains: DomainScore[];
  controls: ControlScore[];
  answered: number;
  total: number;
}

// ---------------------------------------------------------------------------
// Engine
// ---------------------------------------------------------------------------

/** Domain code from a control key like `D1.SD2.C1` → `D1`. */
export const domainOf = (key: string): string => (key || '').split('.')[0] || '';

const stageFor = (mean: number, bands: StageBand[]): StageBand => {
  const ordered = [...bands].sort((a, b) => a.max - b.max);
  for (const band of ordered) {
    if (mean <= band.max) return band;
  }
  return ordered[ordered.length - 1];
};

const toLevel = (raw: any): number | null => {
  if (raw === null || raw === undefined || raw === '') return null;
  const n = Number(Array.isArray(raw) ? raw[0] : raw);
  return Number.isFinite(n) ? n : null;
};

/**
 * Score a filled-in maturity assessment.
 *
 * `maturityQuestions` are the questions of the scored section (the caller filters by
 * `maturity_section_key`); `answersByKey` maps question key → the stored answer. Only answered
 * controls contribute to a domain's mean; a domain with no answers is omitted from the weighted
 * average so an untouched pillar does not drag the score to the floor artificially — its absence is
 * instead visible in the per-domain coverage counts.
 */
export const scoreMaturity = (
  maturityQuestions: AssessmentQuestion[],
  answersByKey: Record<string, any>,
  config: MaturityScoringConfig
): ScoreResult => {
  const controls: ControlScore[] = maturityQuestions.map((q) => ({
    key: q.key,
    text: q.text,
    domain: domainOf(q.key),
    level: toLevel(answersByKey[q.key]),
  }));

  const byDomain = new Map<string, ControlScore[]>();
  controls.forEach((c) => {
    if (!byDomain.has(c.domain)) byDomain.set(c.domain, []);
    byDomain.get(c.domain)!.push(c);
  });

  const domains: DomainScore[] = [];
  byDomain.forEach((list, domain) => {
    const answered = list.filter((c) => c.level != null);
    const mean = answered.length
      ? answered.reduce((sum, c) => sum + (c.level as number), 0) / answered.length
      : config.level_min;
    const band = stageFor(mean, config.stage_bands);
    domains.push({
      domain,
      label: config.domain_labels?.[domain] || domain,
      mean,
      stage: band.stage,
      stageName: band.name,
      answered: answered.length,
      total: list.length,
    });
  });
  domains.sort((a, b) => a.domain.localeCompare(b.domain));

  // Weighted overall across domains that were answered at all.
  const scored = domains.filter((d) => d.answered > 0);
  const weightSum = scored.reduce((sum, d) => sum + (config.pillar_weights?.[d.domain] ?? 0), 0);
  const overallMean = weightSum
    ? scored.reduce((sum, d) => sum + d.mean * (config.pillar_weights?.[d.domain] ?? 0), 0) / weightSum
    : config.level_min;

  const rawBand = stageFor(overallMean, config.stage_bands);
  let stage = rawBand.stage;
  let cappedBy: ScoreResult['cappedBy'] = null;

  if (config.governance_floor && scored.length) {
    const weakest = Math.min(...scored.map((d) => d.stage));
    const gov = scored.find((d) => d.domain === config.governance_domain)?.stage;
    const govCap = gov != null ? gov + 1 : Infinity;
    const weakCap = weakest + 1;
    const capped = Math.min(stage, govCap, weakCap);
    if (capped < stage) {
      const byGov = govCap === capped;
      const byWeak = weakCap === capped;
      cappedBy = byGov && byWeak ? 'both' : byGov ? 'governance' : 'weakest_pillar';
      stage = capped;
    }
  }

  const finalBand = config.stage_bands.find((b) => b.stage === stage) || rawBand;

  return {
    engine: 'maturity_stage',
    overallMean,
    rawStage: rawBand.stage,
    stage,
    stageName: finalBand.name,
    cappedBy,
    domains,
    controls,
    answered: controls.filter((c) => c.level != null).length,
    total: controls.length,
  };
};

// ---------------------------------------------------------------------------
// Report layer — narrative + recommendations, driven by `assessment.metadata.report`
// ---------------------------------------------------------------------------

/**
 * The report content, mirrored from `assessment.metadata.report` (see the seed). It is the prose an
 * assessment attaches to its numeric result: a stage-level narrative and per-domain guidance indexed
 * by the stage that domain landed on. Keeping it as data (not code) is what lets the same modular
 * `buildReport` transform turn any maturity assessment's answers into the same shape of report.
 */
export interface ReportConfig {
  /** Overall prose for the final stage, keyed by stage number as a string. */
  stage_narratives?: Record<string, string>;
  /** domain → stage-number-string → recommendation for a domain sitting at that stage. */
  domain_guidance?: Record<string, Record<string, string>>;
  /** How many of the weakest domains to surface as focus areas (default 2). */
  focus_count?: number;
}

export interface ReportItem {
  domain: string;
  label: string;
  stage: number;
  stageName: string;
  mean: number;
  /** The guidance for this domain at its stage, when the config supplies it. */
  recommendation: string | null;
}

export interface AssessmentReport {
  stage: number;
  stageName: string;
  narrative: string | null;
  /** Weakest domains first — where to invest next. */
  focusAreas: ReportItem[];
  /** Strongest domains — what to sustain. */
  strengths: ReportItem[];
}

export const getReportConfig = (
  assessment: { metadata?: any } | null | undefined
): ReportConfig | null => {
  const cfg = assessment?.metadata?.report;
  return cfg && typeof cfg === 'object' ? (cfg as ReportConfig) : null;
};

const guidanceFor = (config: ReportConfig, domain: string, stage: number): string | null =>
  config.domain_guidance?.[domain]?.[String(stage)] ?? null;

/**
 * Turn a computed `ScoreResult` into a narrative report: the stage-level prose plus the weakest
 * domains to focus on and the strongest to sustain, each with its per-stage recommendation. Pure and
 * repeatable — the same transform runs for the respondent, the admin review, and (mirrored in
 * Python) the seeded example submissions, so all three read the same. Returns null when there is no
 * report config.
 */
export const buildReport = (
  result: ScoreResult | null,
  config: ReportConfig | null
): AssessmentReport | null => {
  if (!result || !config) return null;

  const toItem = (d: DomainScore): ReportItem => ({
    domain: d.domain,
    label: d.label,
    stage: d.stage,
    stageName: d.stageName,
    mean: d.mean,
    recommendation: guidanceFor(config, d.domain, d.stage),
  });

  const scored = result.domains.filter((d) => d.answered > 0);
  const focusCount = config.focus_count ?? 2;

  const weakest = [...scored]
    .sort((a, b) => a.stage - b.stage || a.mean - b.mean)
    .slice(0, focusCount)
    .map(toItem);

  // Strengths: domains at or above the overall stage, strongest first; never overlap the focus list.
  const focusDomains = new Set(weakest.map((i) => i.domain));
  const strengths = [...scored]
    .sort((a, b) => b.stage - a.stage || b.mean - a.mean)
    .filter((d) => d.stage >= result.stage && !focusDomains.has(d.domain))
    .slice(0, focusCount)
    .map(toItem);

  return {
    stage: result.stage,
    stageName: result.stageName,
    narrative: config.stage_narratives?.[String(result.stage)] ?? null,
    focusAreas: weakest,
    strengths,
  };
};

/**
 * Score an assessment given its config, all its questions, the section-key lookup, and answers.
 * Returns null when the assessment has no supported scoring config, so a caller can fall back to a
 * plain "submitted" acknowledgement.
 */
export const scoreAssessment = (
  config: ScoringConfig | null,
  questions: AssessmentQuestion[],
  sectionKeyById: Record<number, string>,
  answersByKey: Record<string, any>
): ScoreResult | null => {
  if (!config) return null;
  if (config.engine === 'maturity_stage') {
    const maturityQuestions = questions.filter(
      (q) => sectionKeyById[q.section] === config.maturity_section_key
    );
    if (!maturityQuestions.length) return null;
    return scoreMaturity(maturityQuestions, answersByKey, config);
  }
  return null;
};
